import postalAreas from '../data/postal-areas.json';
import type {
  Codebook,
  EncodedResponse,
  MultiField,
  ResponsesArtifact,
  SingleField,
} from '../data/types';

export type DashboardTab = 'audience' | 'visit' | 'satisfaction' | 'awareness' | 'needs';

export interface FilterClause {
  field: SingleField | MultiField | 'day' | 'hour';
  values: number[];
}

export interface AreaFilter {
  prefectures: string[];
  municipalities: string[];
}

export interface FilterState {
  version: 1;
  clauses: FilterClause[];
  area: AreaFilter;
}

export interface ComparisonState {
  enabled: boolean;
  baseline: FilterState;
  comparison: FilterState;
}

export interface FilterUrlState {
  tab: DashboardTab;
  filters: FilterState;
  areaLocation?: string;
  comparison?: ComparisonState;
}

export interface FilterResult {
  ids: ReadonlySet<number>;
  total: number;
  rows: EncodedResponse[];
}

export interface ChartValue {
  code: number;
  label: string;
  n: number;
  denominator: number;
  rate: number;
  small: boolean;
}

export interface ComparisonValue {
  baseline: ChartValue;
  comparison: ChartValue;
  percentagePointDifference: number;
}

const SINGLE_SET = new Set<string>([
  'gender', 'age_group', 'transport_method', 'visit_frequency', 'companion',
  'visit_purpose_main', 'visit_budget', 'office_awareness', 'satisfaction',
]);
const MULTI_SET = new Set<string>([
  'favorite_shops', 'sns_usage', 'facility_awareness', 'desired_facilities',
  'desired_events', 'awareness_source', 'campaign_source',
]);
const SMALL_N = 30;
const areas = postalAreas as Record<string, { pref: string; city: string }>;

export const EMPTY_FILTERS: FilterState = {
  version: 1,
  clauses: [],
  area: { prefectures: [], municipalities: [] },
};

function uniqueSorted(values: number[]): number[] {
  return [...new Set(values.filter(Number.isInteger))].sort((a, b) => a - b);
}

function normaliseClause(value: FilterClause): FilterClause | null {
  const validField = SINGLE_SET.has(value.field) || MULTI_SET.has(value.field) || value.field === 'day' || value.field === 'hour';
  const values = uniqueSorted(value.values);
  return validField && values.length > 0 ? { field: value.field, values } : null;
}

export function normaliseFilters(value: Partial<FilterState> | null | undefined): FilterState {
  const clauses = (value?.clauses ?? [])
    .map(normaliseClause)
    .filter((clause): clause is FilterClause => clause !== null)
    .sort((a, b) => a.field.localeCompare(b.field));
  return {
    version: 1,
    clauses,
    area: {
      prefectures: [...new Set(value?.area?.prefectures ?? [])].sort(),
      municipalities: [...new Set(value?.area?.municipalities ?? [])].sort(),
    },
  };
}

function rowArea(row: EncodedResponse): { pref: string; city: string } | null {
  return row.postal ? areas[row.postal] ?? null : null;
}

function matchesClause(row: EncodedResponse, clause: FilterClause): boolean {
  if (clause.field === 'day' || clause.field === 'hour' || SINGLE_SET.has(clause.field)) {
    return clause.values.includes(row[clause.field as SingleField | 'day' | 'hour'] as number);
  }
  const values = row[clause.field as MultiField];
  return values.some((value) => clause.values.includes(value));
}

function matchesArea(row: EncodedResponse, area: AreaFilter): boolean {
  if (area.prefectures.length === 0 && area.municipalities.length === 0) return true;
  const location = rowArea(row);
  if (!location) return false;
  const prefMatches = area.prefectures.length === 0 || area.prefectures.includes(location.pref);
  const cityMatches = area.municipalities.length === 0 || area.municipalities.includes(location.city);
  return prefMatches && cityMatches;
}

/** OR within a clause, AND across clauses and the optional geography constraint. */
export class FilterEngine {
  readonly rows: readonly EncodedResponse[];
  readonly codebook: Codebook;

  constructor(artifact: ResponsesArtifact) {
    this.rows = artifact.rows;
    this.codebook = artifact.codebook;
  }

  apply(filters: Partial<FilterState> | null | undefined): FilterResult {
    const state = normaliseFilters(filters);
    const rows = this.rows.filter((row) =>
      matchesArea(row, state.area) && state.clauses.every((clause) => matchesClause(row, clause)),
    );
    return { rows, ids: new Set(rows.map((row) => row.id)), total: rows.length };
  }

  values(field: SingleField | MultiField | 'day' | 'hour', filters: Partial<FilterState> | null | undefined): ChartValue[] {
    const result = this.apply(filters);
    const values = field === 'hour'
      ? Array.from({ length: 24 }, (_, code) => ({ code, label: `${code}時` }))
      : field === 'day'
        ? this.codebook.day.map((label, code) => ({ code, label }))
        : this.codebook[field].map((label, code) => ({ code, label }));
    const count = new Map<number, number>();
    for (const row of result.rows) {
      const selected = MULTI_SET.has(field)
        ? row[field as MultiField]
        : [row[field as SingleField | 'day' | 'hour'] as number];
      for (const code of selected) count.set(code, (count.get(code) ?? 0) + 1);
    }
    return values.map(({ code, label }) => {
      const n = count.get(code) ?? 0;
      return { code, label, n, denominator: result.total, rate: result.total ? Math.round((n / result.total) * 1000) / 10 : 0, small: n < SMALL_N };
    });
  }

  comparison(field: SingleField | MultiField | 'day' | 'hour', state: ComparisonState): ComparisonValue[] {
    const baseline = this.values(field, state.baseline);
    const comparison = this.values(field, state.comparison);
    return baseline.map((left, index) => {
      const right = comparison[index]!;
      return { baseline: left, comparison: right, percentagePointDifference: Math.round((right.rate - left.rate) * 10) / 10 };
    });
  }
}

export function withClause(filters: FilterState, field: FilterClause['field'], values: number[]): FilterState {
  const clauses = filters.clauses.filter((clause) => clause.field !== field);
  if (values.length > 0) clauses.push({ field, values });
  return normaliseFilters({ ...filters, clauses });
}

export function filterLabel(clause: FilterClause, codebook: Codebook): string {
  const labels = clause.values.map((value) => {
    if (value === -1) return '未回答';
    if (clause.field === 'hour') return `${value}時`;
    return codebook[clause.field as keyof Codebook]?.[value] ?? String(value);
  });
  return labels.join('・');
}

function encode(value: unknown): string { return encodeURIComponent(JSON.stringify(value)); }
function decode<T>(value: string | null): T | null {
  if (!value) return null;
  try { return JSON.parse(decodeURIComponent(value)) as T; } catch { return null; }
}

export function encodeFilterUrl(state: FilterUrlState): URLSearchParams {
  const params = new URLSearchParams();
  params.set('report', '1');
  params.set('tab', state.tab);
  const filters = normaliseFilters(state.filters);
  if (filters.clauses.length || filters.area.prefectures.length || filters.area.municipalities.length) params.set('filters', encode(filters));
  if (state.areaLocation) params.set('map', state.areaLocation);
  if (state.comparison?.enabled) params.set('compare', encode({ ...state.comparison, baseline: normaliseFilters(state.comparison.baseline), comparison: normaliseFilters(state.comparison.comparison) }));
  return params;
}

export function decodeFilterUrl(search: string): FilterUrlState {
  const params = new URLSearchParams(search);
  const tabValue = params.get('tab');
  const tab: DashboardTab = ['audience', 'visit', 'satisfaction', 'awareness', 'needs'].includes(tabValue ?? '') ? tabValue as DashboardTab : 'audience';
  const filters = normaliseFilters(decode<FilterState>(params.get('filters')));
  const rawComparison = decode<ComparisonState>(params.get('compare'));
  const comparison = rawComparison?.enabled ? {
    enabled: true,
    baseline: normaliseFilters(rawComparison.baseline),
    comparison: normaliseFilters(rawComparison.comparison),
  } : undefined;
  return { tab, filters, areaLocation: params.get('map') ?? undefined, comparison };
}
